import { ReactNode } from 'react';
import cn from 'classnames';

import styles from './SimpleLink.module.scss';

export interface SimpleLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href?: string;
  children: ReactNode;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
  mode?: 'light' | 'dark';
  className?: string;
  ref?: React.Ref<HTMLAnchorElement>;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * SimpleLink component for styled anchor links
 * @param variant - 'primary' for no underline, 'secondary' for underlined
 * @param size - Only applies to 'secondary' variant: 'small' or 'medium'
 * @param mode - 'light' for dark text on light backgrounds (default), 'dark' for light text on dark backgrounds
 * @default variant 'primary'
 * @default mode 'light'
 */

const SimpleLink = ({
  href,
  children,
  variant = 'primary',
  size = 'medium',
  mode = 'light',
  className,
  target,
  rel,
  ref,
  onClick,
  ...rest
}: SimpleLinkProps) => {
  if (!href || href.trim() === '') {
    console.warn('SimpleLink: href prop is required and cannot be empty');
    return null;
  }

  const SIZE_STYLES = {
    small: styles.sizeSmall,
    medium: styles.sizeMedium,
  } as const;

  const VARIANT_STYLES = {
    primary: styles.primary,
    secondary: styles.secondary,
  } as const;

  const MODE_STYLES = {
    light: styles.light,
    dark: styles.dark,
  } as const;

  const sizeClass = SIZE_STYLES[size];
  const modeClass = MODE_STYLES[mode];

  const isExternal = target === '_blank';
  const relValue = isExternal
    ? rel
      ? `${rel} noopener noreferrer`
      : 'noopener noreferrer'
    : rel;

  return (
    <a
      ref={ref}
      href={href}
      className={cn(
        styles.simpleLink,
        VARIANT_STYLES[variant],
        modeClass,
        sizeClass,
        className
      )}
      target={target}
      rel={relValue}
      onClick={onClick ?? undefined}
      {...rest}
    >
      {children}
    </a>
  );
};

export default SimpleLink;
